import { Button, Input, Select } from '@sjuni/ui';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RichTextField } from '@/components/editor/RichTextField';
import { MediaPicker } from '@/components/media/MediaPicker';
import { TranslatableField } from '@/components/form/TranslatableField';
import type { TranslatedValue } from '@/lib/forms/translatable';
import type { MediaRow } from '@/routes/media/types';

import {
  type AccordionItem,
  type GalleryImage,
  CTA_VARIANTS,
  EMBED_PROVIDERS,
  GALLERY_LAYOUTS,
  IMAGE_WIDTHS,
  type PageBlock,
  type StatItem,
} from '../types';

/**
 * The form for one block of docs/03-DATA-MODEL.md §7.1.
 *
 * Ten shapes in one file rather than ten files, because they are ten variations
 * on the same four controls and reading them side by side is how you notice
 * that the accordion's body and the rich text block's body are the same thing.
 *
 * Every field writes through `set`, which replaces one key of `data`. The block
 * itself is never rebuilt from a template: an unknown block type — or a known
 * one carrying a key this deploy has no control for — keeps whatever it had,
 * which is what §7.1's "unknown block types render nothing" implies for the
 * editor as much as for the renderer.
 */
interface Props {
  block: PageBlock;
  onChange: (data: Record<string, unknown>) => void;
  /** The library images this page's blocks reference, keyed by id. */
  media: Record<number, MediaRow>;
  /** Remembers an image the editor just picked, so its thumbnail appears now. */
  onMediaPicked: (media: MediaRow) => void;
}

export function BlockFields({ block, onChange, media, onMediaPicked }: Props) {
  const { t } = useTranslation();

  /** The picked image for a block's `media_id`, or null while none is chosen. */
  const picked = (data: Record<string, unknown>): MediaRow | null => {
    const id = data.media_id;

    return typeof id === 'number' ? (media[id] ?? null) : null;
  };

  const set = (key: string, value: unknown): void => onChange({ ...block.data, [key]: value });

  const translated = (key: string): TranslatedValue => {
    const value = block.data[key];

    return typeof value === 'object' && value !== null ? (value as TranslatedValue) : {};
  };

  const text = (key: string): string => {
    const value = block.data[key];

    return typeof value === 'string' ? value : '';
  };

  switch (block.type) {
    case 'rich_text':
      return (
        <RichTextField
          label={t('pages.blocks.fields.html')}
          value={translated('html')}
          onChange={(next) => set('html', next)}
        />
      );

    case 'image':
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          {/*
            The same picker every other image field uses. It replaced a text
            box an editor typed a storage path into — the one image field on
            the site with no upload, no preview and nothing checking the file
            was there.
          */}
          <MediaPicker
            label={t('pages.blocks.fields.image')}
            hint={t('pages.blocks.fields.imageHint')}
            value={picked(block.data)}
            onChange={(mediaId, row) => {
              if (row) {
                onMediaPicked(row);
              }

              set('media_id', mediaId);
            }}
          />

          <Select
            label={t('pages.blocks.fields.width')}
            value={text('width') || 'normal'}
            onChange={(event) => set('width', event.target.value)}
            options={IMAGE_WIDTHS.map((width) => ({
              value: width,
              label: t(`pages.blocks.widths.${width}`),
            }))}
          />

          {/*
            §7.5's alt text, which is now an override rather than the only
            description: the image cannot be chosen at all without one in the
            library. Worth keeping, because the same photograph can mean
            different things on two pages — but an editor should know they are
            not obliged to fill it in, hence the hint.
          */}
          <div className="sm:col-span-2">
            <TranslatableField
              label={t('pages.blocks.fields.alt')}
              hint={t('pages.blocks.fields.altHint')}
              name="alt"
              value={translated('alt')}
              onChange={(next) => set('alt', next)}
              required={false}
            />
          </div>

          <div className="sm:col-span-2">
            <TranslatableField
              label={t('pages.blocks.fields.caption')}
              name="caption"
              value={translated('caption')}
              onChange={(next) => set('caption', next)}
              required={false}
            />
          </div>
        </div>
      );

    case 'gallery':
      return (
        <div className="space-y-4">
          <Select
            label={t('pages.blocks.fields.layout')}
            value={text('layout') || 'grid'}
            onChange={(event) => set('layout', event.target.value)}
            options={GALLERY_LAYOUTS.map((layout) => ({
              value: layout,
              label: t(`pages.blocks.layouts.${layout}`),
            }))}
          />

          <GalleryImages
            label={t('pages.blocks.fields.images')}
            hint={t('pages.blocks.fields.imagesHint')}
            addLabel={t('pages.blocks.fields.addImage')}
            removeLabel={t('pages.blocks.fields.removeImage')}
            value={galleryImages(block.data.images)}
            media={media}
            onMediaPicked={onMediaPicked}
            onChange={(images) => set('images', images)}
          />
        </div>
      );

    case 'document_list':
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            type="number"
            min={1}
            label={t('pages.blocks.fields.categoryId')}
            hint={t('pages.blocks.fields.categoryIdHint')}
            value={numberText(block.data.category_id)}
            onChange={(event) =>
              set('category_id', event.target.value === '' ? null : Number(event.target.value))
            }
          />

          <Input
            label={t('pages.blocks.fields.documentIds')}
            hint={t('pages.blocks.fields.documentIdsHint')}
            value={idList(block.data.document_ids)}
            onChange={(event) => set('document_ids', parseIds(event.target.value))}
          />
        </div>
      );

    case 'accordion':
      return (
        <ItemList
          label={t('pages.blocks.fields.items')}
          addLabel={t('pages.blocks.addItem')}
          items={accordionItems(block.data.items)}
          empty={{ title: {}, body: {} }}
          onChange={(items) => set('items', items)}
          render={(item, update) => (
            <div className="space-y-3">
              <TranslatableField
                label={t('pages.blocks.fields.itemTitle')}
                name="title"
                value={item.title}
                onChange={(next) => update({ ...item, title: next })}
              />

              <RichTextField
                label={t('pages.blocks.fields.itemBody')}
                value={item.body}
                onChange={(next) => update({ ...item, body: next })}
              />
            </div>
          )}
        />
      );

    case 'table':
      return (
        <TableFields
          headers={stringList(block.data.headers)}
          rows={rowList(block.data.rows)}
          onChange={(headers, rows) => onChange({ ...block.data, headers, rows })}
        />
      );

    case 'quote':
      return (
        <div className="space-y-4">
          <TranslatableField
            label={t('pages.blocks.fields.quote')}
            name="text"
            multiline
            rows={3}
            value={translated('text')}
            onChange={(next) => set('text', next)}
          />

          <TranslatableField
            label={t('pages.blocks.fields.attribution')}
            name="attribution"
            value={translated('attribution')}
            onChange={(next) => set('attribution', next)}
            required={false}
          />
        </div>
      );

    case 'embed':
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('pages.blocks.fields.provider')}
            value={text('provider') || 'youtube'}
            onChange={(event) => set('provider', event.target.value)}
            options={EMBED_PROVIDERS.map((provider) => ({
              value: provider,
              label: t(`pages.blocks.providers.${provider}`),
            }))}
          />

          {/*
            The address-bar URL, which is what an editor has. The server
            normalises it to something an iframe can load and refuses the ones
            it cannot — a YouTube watch link is valid and unembeddable, and
            silently rendering an empty box is worse than saying so.
          */}
          <Input
            label={t('pages.blocks.fields.url')}
            hint={t('pages.blocks.fields.embedUrlHint')}
            value={text('url')}
            onChange={(event) => set('url', event.target.value)}
          />
        </div>
      );

    case 'cta':
      return (
        <div className="space-y-4">
          <TranslatableField
            label={t('pages.blocks.fields.label')}
            name="label"
            value={translated('label')}
            onChange={(next) => set('label', next)}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={t('pages.blocks.fields.url')}
              hint={t('pages.blocks.fields.ctaUrlHint')}
              value={text('url')}
              onChange={(event) => set('url', event.target.value)}
            />

            <Select
              label={t('pages.blocks.fields.variant')}
              value={text('variant') || 'primary'}
              onChange={(event) => set('variant', event.target.value)}
              options={CTA_VARIANTS.map((variant) => ({
                value: variant,
                label: t(`pages.blocks.variants.${variant}`),
              }))}
            />
          </div>
        </div>
      );

    case 'stats':
      return (
        <ItemList
          label={t('pages.blocks.fields.stats')}
          addLabel={t('pages.blocks.addStat')}
          items={statItems(block.data.items)}
          empty={{ value: '', label: {} }}
          onChange={(items) => set('items', items)}
          render={(item, update) => (
            <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
              <Input
                label={t('pages.blocks.fields.statValue')}
                value={item.value}
                onChange={(event) => update({ ...item, value: event.target.value })}
              />

              <TranslatableField
                label={t('pages.blocks.fields.statLabel')}
                name="label"
                value={item.label}
                onChange={(next) => update({ ...item, label: next })}
              />
            </div>
          )}
        />
      );

    default:
      /*
       * §7.1 — an unknown type renders nothing and is not deleted. The editor
       * says so rather than showing an empty panel that reads as a bug, and
       * the block's payload is carried back unchanged on save.
       */
      return (
        <p className="bg-surface-subtle rounded-card p-4 text-body-sm text-secondary">
          {t('pages.blocks.unknown', { type: block.type })}
        </p>
      );
  }
}

/* ── the small controls the shapes above share ───────────────────────── */

/** An ordered list of repeated sub-forms: accordion panels, statistics. */
function ItemList<TItem>({
  label,
  addLabel,
  items,
  empty,
  onChange,
  render,
}: {
  label: string;
  addLabel: string;
  items: TItem[];
  empty: TItem;
  onChange: (items: TItem[]) => void;
  render: (item: TItem, update: (next: TItem) => void) => React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <fieldset className="space-y-3">
      <legend className="text-caption font-extrabold tracking-wide text-muted">{label}</legend>

      {items.map((item, index) => (
        <div key={index} className="rounded-card border border-subtle p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-caption text-muted">{index + 1}</span>

            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={() => onChange(items.filter((_, position) => position !== index))}
            >
              {t('common.delete')}
            </Button>
          </div>

          {render(item, (next) =>
            onChange(items.map((current, position) => (position === index ? next : current))),
          )}
        </div>
      ))}

      <Button
        type="button"
        size="sm"
        variant="secondary"
        iconLeft={<Plus size={14} strokeWidth={1.75} aria-hidden="true" />}
        onClick={() => onChange([...items, empty])}
      >
        {addLabel}
      </Button>
    </fieldset>
  );
}

/**
 * A gallery's images, one picker each.
 *
 * A textarea of paths, one per line, was the old shape. It was the fastest
 * thing to build and the least usable thing on the screen: an editor had to
 * know a storage path per photograph, and a typo showed up as a broken image
 * on the public site rather than as an error here.
 */
function GalleryImages({
  label,
  hint,
  addLabel,
  removeLabel,
  value,
  media,
  onMediaPicked,
  onChange,
}: {
  label: string;
  hint: string;
  addLabel: string;
  removeLabel: string;
  value: GalleryImage[];
  media: Record<number, MediaRow>;
  onMediaPicked: (media: MediaRow) => void;
  onChange: (images: GalleryImage[]) => void;
}) {
  const replace = (index: number, next: GalleryImage): void =>
    onChange(value.map((image, position) => (position === index ? next : image)));

  return (
    <fieldset>
      <legend className="mb-1.5 block text-body-sm font-semibold text-primary">{label}</legend>
      <span className="mb-3 block text-caption text-muted">{hint}</span>

      <div className="space-y-3">
        {value.map((image, index) => (
          <div key={index} className="flex items-start gap-3">
            <MediaPicker
              label={`${label} ${index + 1}`}
              value={image.media_id === null ? null : (media[image.media_id] ?? null)}
              onChange={(mediaId, row) => {
                if (row) {
                  onMediaPicked(row);
                }

                replace(index, { ...image, media_id: mediaId });
              }}
            />

            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={() => onChange(value.filter((_, position) => position !== index))}
            >
              <span className="sr-only">{`${removeLabel} ${index + 1}`}</span>
            </Button>
          </div>
        ))}
      </div>

      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="mt-3"
        iconLeft={<Plus size={14} strokeWidth={1.75} aria-hidden="true" />}
        onClick={() => onChange([...value, { media_id: null }])}
      >
        {addLabel}
      </Button>
    </fieldset>
  );
}

/**
 * §7.1's `{headers, rows}`.
 *
 * Adding a column adds a cell to every row, because the server refuses a table
 * whose rows and headers disagree — and a form that let an editor build one
 * would be refusing their work after the fact rather than preventing it.
 */
function TableFields({
  headers,
  rows,
  onChange,
}: {
  headers: string[];
  rows: string[][];
  onChange: (headers: string[], rows: string[][]) => void;
}) {
  const { t } = useTranslation();

  const width = Math.max(headers.length, 1);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() =>
            onChange(
              [...headers, ''],
              rows.map((row) => [...row, '']),
            )
          }
        >
          {t('pages.blocks.addColumn')}
        </Button>

        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => onChange(headers, [...rows, Array.from({ length: width }, () => '')])}
        >
          {t('pages.blocks.addRow')}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-body-sm">
          <thead>
            <tr>
              {headers.map((header, column) => (
                <th key={column} className="p-1">
                  <Input
                    label={t('pages.blocks.fields.column', { number: column + 1 })}
                    hideLabel
                    value={header}
                    onChange={(event) =>
                      onChange(
                        headers.map((current, position) =>
                          position === column ? event.target.value : current,
                        ),
                        rows,
                      )
                    }
                  />
                </th>
              ))}
              <th className="w-10" />
            </tr>
          </thead>

          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: width }, (_, column) => (
                  <td key={column} className="p-1">
                    <Input
                      label={t('pages.blocks.fields.cell', {
                        row: rowIndex + 1,
                        column: column + 1,
                      })}
                      hideLabel
                      value={row[column] ?? ''}
                      onChange={(event) =>
                        onChange(
                          headers,
                          rows.map((current, position) =>
                            position === rowIndex
                              ? Array.from({ length: width }, (_, cell) =>
                                  cell === column ? event.target.value : (current[cell] ?? ''),
                                )
                              : current,
                          ),
                        )
                      }
                    />
                  </td>
                ))}

                <td className="p-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('pages.blocks.removeRow', { number: rowIndex + 1 })}
                    onClick={() =>
                      onChange(
                        headers,
                        rows.filter((_, position) => position !== rowIndex),
                      )
                    }
                  >
                    <Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── readers ─────────────────────────────────────────────────────────── */

/*
 * Everything below reads a value out of `data`, which is `unknown` because the
 * API holds a block loosely on purpose. A migrated page is the reason these
 * degrade rather than throw: docs/07-MIGRATION.md treats the WordPress source
 * as hostile, and a block whose `items` arrived as a string should render an
 * empty list an editor can fix, not a white screen.
 */

/**
 * A gallery's entries, degrading rather than throwing.
 *
 * `media_id` is the only key this reads: an entry that still carries a `path`
 * is pre-migration data, and it comes back as an empty slot the editor can
 * fill — which is exactly what the migration reports it could not do
 * automatically.
 */
function galleryImages(value: unknown): GalleryImage[] {
  return Array.isArray(value)
    ? value.map((image) => {
        const id =
          typeof image === 'object' && image !== null && 'media_id' in image
            ? (image as { media_id: unknown }).media_id
            : null;

        return {
          ...(typeof image === 'object' && image !== null ? image : {}),
          media_id: typeof id === 'number' ? id : null,
        };
      })
    : [];
}

function accordionItems(value: unknown): AccordionItem[] {
  return Array.isArray(value)
    ? value.map((item) => ({
        title: readTranslated(item, 'title'),
        body: readTranslated(item, 'body'),
      }))
    : [];
}

function statItems(value: unknown): StatItem[] {
  return Array.isArray(value)
    ? value.map((item) => ({
        value:
          typeof item === 'object' && item !== null && 'value' in item
            ? String((item as { value: unknown }).value ?? '')
            : '',
        label: readTranslated(item, 'label'),
      }))
    : [];
}

function readTranslated(item: unknown, key: string): TranslatedValue {
  if (typeof item !== 'object' || item === null || !(key in item)) {
    return {};
  }

  const value = (item as Record<string, unknown>)[key];

  return typeof value === 'object' && value !== null ? (value as TranslatedValue) : {};
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item ?? '')) : [];
}

function rowList(value: unknown): string[][] {
  return Array.isArray(value) ? value.map((row) => stringList(row)) : [];
}

function numberText(value: unknown): string {
  return typeof value === 'number' ? String(value) : '';
}

function idList(value: unknown): string {
  return Array.isArray(value) ? value.join(', ') : '';
}

function parseIds(value: string): number[] {
  return value
    .split(/[\s,]+/)
    .map((part) => Number(part))
    .filter((id) => Number.isInteger(id) && id > 0);
}
